import axios from 'axios';
import { API_ROOT_URL, PAGESIZE, BASE_COIN_SYMBOL } from './constants/config'
const API_BRICKION_ROOT = API_ROOT_URL
const client = axios.create();
client.defaults.baseURL = API_BRICKION_ROOT;
client.defaults.headers.post['Content-Type'] = 'application/json';
client.defaults.headers.post['Accept'] = 'application/json';
let token = null;

client.interceptors.request.use(config => {
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, err => {
  return Promise.reject(err);
})
const responseData = res => res.data;

const b_requests = {
  del: url => {
    return client.delete(url).then(responseData)
  },
  get: url =>
    client.get(url).then(responseData),
  put: (url, body) =>
    client.put(url, body).then(responseData),
  post: (url, body) =>
    client.post(url, body).then(responseData)
}

const Auth = {
  current: () =>
    b_requests.get('/users'),
  login: (email, password) =>
    b_requests.post('/users/login', JSON.stringify({ email, password })),
  getPinCode: (mobile, type, userkey) =>
    b_requests.post(`/users/pincode/get`, JSON.stringify({ mobile, type, userkey })),
  verifyPinCode: (userKey, code, type) =>
    b_requests.post(`/users/${userKey}/pincode/verify`, JSON.stringify({ code, type })),
  logout: () => b_requests.post(`/users/logout`),
  verifyUserPin: (token, userKey, pin) => {
    const instance = axios.create({
      baseURL: API_BRICKION_ROOT
    });
    instance.defaults.headers.post['Content-Type'] = 'application/json';
    instance.defaults.headers.post['Accept'] = 'application/json';
    instance.defaults.headers.Authorization = `Bearer ${token}`;
    return instance.post(`/users/${userKey}/pin/verify`, JSON.stringify({ pin })).then(responseData)
  },

};

const paging = (pagesize, pageindex) => `pagesize=${pagesize}&pageindex=${pageindex}`;
const datestring = (datefrom, dateto) => `datefrom=${datefrom}&dateto=${dateto}`
const pageSize = PAGESIZE;

const Users = {
  all: (page, terms, status, datefrom, dateto) => {
    return b_requests.get(`/users/list?${paging(pageSize, page)}&terms=${terms}&status=${status}&${datestring(datefrom, dateto)}`)
  },
  merchants: () => {
    return b_requests.get(`/users/list?pagesize=9999&pageindex=1&type=merchant`)
  },
  export: (datefrom, dateto, filter) =>
    b_requests.get(`/users/list/export?filter=${filter}&${datestring(datefrom, dateto)}`),
  get: key =>
    b_requests.get(`/users/${key}`),
  create: (email, password, firstname, lastname, phone, pin, countrycode) =>
    b_requests.post('/v2/users', JSON.stringify({ email, password, firstname, lastname, phone, pin, countrycode })),
  updateLockStatus: (key, locked) =>
    b_requests.post(`/users/${key}/lockstatus/update`, JSON.stringify({ locked })),
  updatePhoneStatus: (key, verified) =>
    b_requests.post(`/users/${key}/phonestatus/update`, JSON.stringify({ verified })),
  resetBankVerification: (key) =>
    b_requests.post(`/users/${key}/bank/resetverification`),
  getCommitUsers: (symbol) =>
    b_requests.get(`/users/${symbol}/commit`),
  getCommitTransactions: (key, symbol) =>
    b_requests.get(`/users/${key}/commit/${symbol}/transactions`),
  toggleMFA: (key, enabled) =>
    b_requests.post(`/users/${key}/mfa/update`, JSON.stringify({ enabled })),
  updateRole: (key, role) =>
    b_requests.post(`/users/${key}/role/update`, JSON.stringify({ role })),
  updateReferrer: (key, referrer) =>
    b_requests.post(`/users/${key}/referrer/update`, JSON.stringify({ referrer })),
  getTradeAnalysis: (key, from, to) =>
    b_requests.get(`/users/${key}/tradeanalysis?from=${from}&to=${to}`),
  resendVerificationEmail: (userkey) =>
    b_requests.post(`/users/${userkey}/emailcode/send`),
  updateMerchantFeeRate: (userkey, merchant_fee_rate) =>
    b_requests.put(`/users/${userkey}/merchant_fee_rate`, { merchant_fee_rate }),
  updatePhone: (key, phone) =>
    b_requests.post(`/users/${key}/admin/phone/update`, JSON.stringify({ phone })),
  updatePin: (key, pin) =>
    b_requests.post(`/users/${key}/admin/pin/update`, JSON.stringify({ pin })),
  resetTotp: (key) =>
    b_requests.post(`/users/${key}/totp/reset`),
};

const Accounts = {
  user: key =>
    b_requests.get(`/accounts/user/${key}`),
  get: (key, page) =>
    b_requests.get(`/accounts/${key}?${paging(pageSize, page)}`),
  getMasterAccounts: () =>
    b_requests.get(`/accounts/master/all`),
  getMasterAccountByKey: (key) =>
    b_requests.get(`/accounts/${key}/master`),
  getMasterAccountDetail: (key, page) =>
    b_requests.get(`/accounts/master/${key}/detail?${paging(pageSize, page)}`),
  createMasterAccount: (symbol, public_key, private_key) =>
    b_requests.post(`/accounts/master`, JSON.stringify({ symbol, public_key, private_key })),
  updateMasterAccount: (key, public_key, private_key) =>
    b_requests.post(`/accounts/${key}/master`, JSON.stringify({ public_key, private_key })),
  mintMasterAccount: (key, amount) =>
    b_requests.post(`/accounts/master/${key}/mint`, JSON.stringify({ amount })),
  depositWithBankRef: (code, amount) =>
    b_requests.post(`/accounts/deposit/bankreference`, JSON.stringify({ code, amount })),
  exportWallet: (publicKey) =>
    b_requests.get(`/coin/export/${publicKey}`),
  resetSyncTime: (key) =>
    b_requests.post(`/accounts/${key}/synctime/reset`),
  getAccountByExternal: (external) =>
    b_requests.get(`/accounts/external/${external}`),
  findAccountByKeyOrExternal: (keyword) =>
    b_requests.get(`/accounts/find/${keyword}`),
  exportAccounts: (symbol) =>
    b_requests.get(`/accounts/${symbol}/export?pagesize=all&pageindex=1`),
  getWallet: (address) =>
    b_requests.get(`/coin/${address}`),
  createAccounts: (userkey, symbols) =>
    b_requests.post(`/accounts`, JSON.stringify({ userkey, symbols })),
};

const Coins = {
  transactions: (page, terms, datefrom, dateto) =>
    b_requests.get(`/transactions/coins?${paging(pageSize, page)}&terms=${terms}&${datestring(datefrom, dateto)}`),
  transactionDetail: (key) =>
    b_requests.get(`/transactions/coins/${key}`),
  send: (sender_account, recipient_account, amount) =>
    b_requests.post('/transactions/send', JSON.stringify({ sender_account, recipient_account, amount })),
  sendViaAddress: (sender_address, recipient_address, amount, notes, pin, code) =>
    b_requests.post(`/transactions/sendviaaddress`, JSON.stringify({ sender_address, recipient_address, amount, notes, pin, code })),
}

const Logs = {
  all: (page) =>
    b_requests.get(`/logs/list?${paging(pageSize, page)}`),
}

const Tasks = {
  list: (task, action, status, page, terms, datefrom, dateto) =>
    b_requests.get(`/tasks/${task}/${action}?status=${status}&${paging(pageSize, page)}&terms=${terms}&${datestring(datefrom, dateto)}`),
  complete: (id) =>
    b_requests.post(`/tasks/complete/${id}`),
  reject: (id) =>
    b_requests.post(`/tasks/reject/${id}`),
  export: (task, action) =>
    b_requests.get(`/tasks/export/${task}/${action}`),
  getUserTasks: (userkey) =>
    b_requests.get(`/tasks/user/${userkey}/list`),
  upload: (type, action, file) => {
    let formData = new FormData()
    formData.append('file', file)
    return b_requests.post(`/tasks/import/${type}/${action}`, formData)
  }
}

const Transactions = {
  all: (page, type, terms, datefrom, dateto) =>
    b_requests.get(`/transactions?${paging(pageSize, page)}&type=${type}&terms=${terms}&${datestring(datefrom, dateto)}`),
  export: (type) =>
    b_requests.get(`/transactions/export?type=${type}`),
  exportXifBuy: () =>
    b_requests.get(`/reports/xifbuy/export`),
  detail: (key) =>
    b_requests.get(`/transactions/${key}`),
}

const Trades = {
  all: (page, terms, datefrom, dateto) =>
    b_requests.get(`/trades/list/spncusd?${paging(pageSize, page)}&terms=${terms}&${datestring(datefrom, dateto)}`),
  detail: (key) => b_requests.get(`/trades/detail/${key}`),
}
const Common = {
  statistics: () =>
    b_requests.get(`/statistics`),
  generateSignature: (sender, recipient, amount, symbol, nonce, privatekey) =>
    b_requests.post(`/signature/generate`, JSON.stringify({ sender, recipient, amount, symbol, nonce, privatekey })),
  statistics_xif: () =>
    b_requests.get(`/statistics_xif`),
  getUsersReports: () =>
    b_requests.get(`/site/users/reports`),
  getCountries: () => {
    return b_requests.get(`/site/countries`)
  }
}
const Contracts = {
  list: (type, page, terms, status, datefrom, dateto) =>
    b_requests.get(`/contracts/list/${type}/simple?${paging(pageSize, page)}&terms=${terms}&status=${status}&${datestring(datefrom, dateto)}`),
  detail: (key) => b_requests.get(`/contracts/${key}`),
  create: (name, symbol, contract, abiArray, decimal, imageurl, corporation, hq, corporatefoundingdate, founder,
    website, firstissuedate, marketcapitalisation, listedexchanges, blockcreationcycle, miningcompensationamount, totalsupply,
    agreementprotocol, whitepaperurl, type, display) =>
    b_requests.post('/contracts', JSON.stringify({
      name, symbol, contract, abiArray, decimal, imageurl, corporation, hq, corporatefoundingdate, founder,
      website, firstissuedate, marketcapitalisation, listedexchanges, blockcreationcycle, miningcompensationamount, totalsupply,
      agreementprotocol, whitepaperurl, type, display
    })),
  update: (key, name, symbol, contract, abiArray, decimal, imageurl, corporation, hq, corporatefoundingdate, founder,
    website, firstissuedate, marketcapitalisation, listedexchanges, blockcreationcycle, miningcompensationamount, totalsupply,
    agreementprotocol, whitepaperurl, type, display) =>
    b_requests.post(`/contracts/${key}`, JSON.stringify({
      name, symbol, contract, abiArray, decimal, type, imageurl, corporation, hq, corporatefoundingdate, founder,
      website, firstissuedate, marketcapitalisation, listedexchanges, blockcreationcycle, miningcompensationamount, totalsupply,
      agreementprotocol, whitepaperurl, display
    })),
}
const Chats = {
  chatList: () => b_requests.get(`/chats/list`),
  createChannel: (title, language) => b_requests.post(`/chats`, JSON.stringify({ title, language })),
  updateChannel: (key, title, language) => b_requests.put(`/chats/detail/${key}`, { title, language }),
  deleteChat: (key) => b_requests.post(`/chats/${key}/delete`),
  toggleChatDisplay: (key, display) => b_requests.post(`/chats/${key}/display/update`, JSON.stringify({ display })),
  chatDetail: (key, page, terms, datefrom, dateto) =>
    b_requests.get(`/chats/detail/${key}?${paging(pageSize, page)}&terms=${terms}&${datestring(datefrom, dateto)}`),
  getPostDetail: (key) =>
    b_requests.get(`/chats/posts/${key}`),
  deleteChatPost: (key) =>
    b_requests.post(`/chats/post/${key}/delete`),
  togglePostDisplay: (key, display) => b_requests.post(`/chats/post/${key}/display/update`, JSON.stringify({ display })),

}
const Categories = {
  getCategories: (userkey) =>
    b_requests.get(`/categories/user/${userkey}`),
  getDetail: (key) =>
    b_requests.get(`/categories/${key}`),
  updateCategory: (key, name) =>
    b_requests.put(`/categories/${key}`, { name }),
  deleteCategory: (key) => b_requests.del(`/categories/${key}`),
  getCategoryProducts: (key) =>
    b_requests.get(`/category/${key}/products`),
  getCategoryLogs: (key) =>
    b_requests.get(`/category/${key}/logs`),
}
const Products = {
  getProducts: (userkey) =>
    b_requests.get(`/products/user/${userkey}`),
  getDetail: (key) =>
    b_requests.get(`/products/${key}`),
  updateProduct: (key, name, price, currency, category) =>
    b_requests.put(`/products/${key}`, { name, price, currency, category }),
  deleteProduct: (key) => b_requests.del(`/products/${key}`),
  getProductLogs: (key) =>
    b_requests.get(`/product/${key}/logs`),
}
const Rates = {
  getAllRates: () =>
    b_requests.get(`/rates`),
  getRate: (symbol) =>
    b_requests.get(`/rates/${symbol}`),
  createRate: (symbol, rate) =>
    b_requests.post(`/rates`, { symbol, rate }),
  updateRate: (symbol, rate) =>
    b_requests.put(`/rates/${symbol}`, { rate }),
}
const Tokens = {
  getTokens: () =>
    b_requests.get(`/tokens`),
  getToken: (symbol) => b_requests.get(`/tokens/${symbol}`),
  createToken: (name, symbol, total, fixed, website, description, logo) => {
    let formData = new FormData()
    if (logo) {
      formData.append('file', logo)
    }
    formData.append('name', name)
    formData.append('symbol', symbol)
    formData.append('total', total)
    formData.append('fixed', fixed)
    formData.append('website', website)
    formData.append('description', description)
    return b_requests.post('/tokens', formData)
  },
  updateToken: (symbol, fixed, website, description, logo) => {
    let formData = new FormData()
    if (logo) {
      formData.append('file', logo)
    }
    formData.append('fixed', fixed)
    formData.append('website', website)
    formData.append('description', description)

    return b_requests.put(`/tokens/${symbol}`, formData)
  },
  getTokenLogs: (symbol) => b_requests.get(`/tokens/${symbol}/logs`),
  approveToken: (symbol, code) => b_requests.post(`/tokens/${symbol}/approve`, { code }),
  rejectToken: (symbol, reason, code) => b_requests.post(`/tokens/${symbol}/reject`, { reason, code }),
  enableToken: (symbol, code) => b_requests.post(`/tokens/${symbol}/enable`, { code }),
  pauseToken: (symbol, code) => b_requests.post(`/tokens/${symbol}/pause`, { code }),
  getTokenCreationFees: () => b_requests.get(`/site/token_fees`),
  updateTokenCreationFees: (token_fee, code) => b_requests.put(`/site/token_fees`, { token_fee, code }),
  getPrimeTokens: () => b_requests.get(`/tokens/prime/symbols`),
  getTokenAdmins: () => b_requests.get(`/tokens/admin/list`)
}
export default {
  Accounts,
  Auth,
  Coins,
  Logs,
  Users,
  Tasks,
  Trades,
  Transactions,
  Common,
  Contracts,
  Chats,
  Categories,
  Products,
  Rates,
  Tokens,
  setToken: _token => { token = _token; }
};



// WEBPACK FOOTER //
// ./src/agent.js